import React from 'react';
import { Box, Skeleton, Stack, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import PageContainer, { PageContainerContext } from '../components/common/PageContainer';
import KpiRow, { KpiCell } from '../components/common/KpiRow';
import Panel from '../components/common/Panel';
import { FOOTER_MIN_HEIGHT } from '../components/common/TableFooterSimple';
import { SP } from '../theme/tokens';

/**
 * ScraperListSkeleton — der Inhalt der Ziel-Liste im Ladezustand.
 *
 * 🔴 Welle 2 PR 3b, Nachbesserung 2 (2026-10-06). Zwei Dinge waren falsch, und
 * beide sind gemessen (390 × 844, Browser):
 *
 * 1. **Der Rahmen.** Das Skelett sass 16 px eingerueckt gegenueber dem geladenen
 *    Inhalt (Kacheln bei x = 28, geladene Kennzahl-Karten bei x = 12). Ursache:
 *    der EIGENE `PageContainer` des Skeletts. Er steht seit Nachbesserung 1 in
 *    einem zweiten (`ScraperList.jsx`, Lade-Zweig) und meldet sich ueber
 *    `PageContainerContext` als verschachtelt — sein `px: 0` unterliegt aber dem
 *    `MuiContainer`-Override des Themes, der unter `sm` `paddingLeft/Right: 16px`
 *    setzt (`theme.js`). Ein NICHT-responsives `px: 0` im `sx` wird von der
 *    Media-Query-Regel der `styleOverrides` ueberholt; ein responsives
 *    `px: { xs: … }` gewinnt. Genau deshalb steht der Inhalt des geladenen
 *    Zweigs (direkt im aeusseren Container) bei 12 und der des Skeletts bei
 *    12 + 16 = 28.
 *
 *    🔴 Deshalb traegt das Skelett seinen Container nur, wenn es ALLEIN steht
 *    (`isEmbedded`-Zweig, Makro-Schau). Ist es bereits verschachtelt, gibt es
 *    seinen Inhalt direkt heraus — dann sitzt er auf demselben Rahmen wie der
 *    geladene Inhalt, ohne eigenen `PageContainer`.
 *
 * 2. **Die Anatomie.** Vorher zeichnete das Skelett acht einzelne Kacheln in
 *    zwei `Box`-Reihen. Geladen stehen dort ZWEI Karten (`KpiRow`, je 2 × 2
 *    Zellen auf Mobil), mit denselben Teilern und demselben Innenabstand — und
 *    darunter die Analyse-Karten in einem Raster. Jetzt sind es dieselben
 *    Bausteine wie geladen (`KpiRow`/`KpiCell`, `Panel`), sodass Rahmen,
 *    Innenabstand und Raster ohne eigenes `sx` stimmen.
 *
 * 🔴 R16 C1 (2026-10-06): KEIN Kopf-Platzhalter. Den Kopf rendert die Seite in
 * JEDEM Zustand (`ScraperList.jsx`, Lade-Zweig) — der graue Balken hier war der
 * Grund, warum im Laden GAR KEIN Titel stand (die mobile Leiste schweigt seit
 * 5(i)). Das Skelett zeichnet nur den Inhalt.
 *
 * 🔴 R16 Runde 2, W-1 (2026-10-21): zwei Props grenzen die Flaeche ein.
 * `isEmbedded` (eingebettete Wege: Produktseite, `CompetitorDetail`) und
 * `tableOnly` (die reine Tabellenfassung `ScraperTargetsTable`) zeichnen NUR
 * die Tabelle — keine Kennzahl-Reihe, keine Filterzeile, kein `Panel`. Das ist
 * die geladene Anatomie: `ScraperList.jsx::embeddedSection` besteht aus
 * Tabelle, optionalem Knopf und optionaler Blaetterleiste. Die Vorgabe (beide
 * `false`) ist die Seitenfassung und bleibt unveraendert.
 */

/**
 * 🔴 UI2 Teil 10, G10a (§2.3/§10.1): geladen steht EINE KPI-Reihe mit vier
 * Zellen, nicht mehr zwei Reihen à vier. Das Geruest zeichnet dieselbe Zahl —
 * sonst springt die Seite in dem Moment, in dem die Daten eintreffen (genau der
 * Gegenstand von `skelett-anatomie.spec.ts` K.3 und `ladezustaende.test.jsx` P4).
 */
const KENNZAHL_KARTEN = 1;
const KENNZAHL_ZELLEN = 4;
const TABELLEN_ZEILEN = 8;
// 🔴 W14 (G10c): die geladene Tabelle traegt SIEBEN Spalten (Status · Product ·
// Market · Competitor · Last Price · Failures · Actions). Mit sechs sprang die
// Tabelle beim Eintreffen der Daten um eine Spalte — der Sprung war die
// staerkste Bewegung auf der Seite und die einzige, die niemand gemessen hatte.
const TABELLEN_SPALTEN = 7;

/**
 * Eine Kennzahl-Zelle im Ladezustand. Dieselbe Anatomie wie `StatTile` im
 * Ladezweig (Label, Wert, Hinweis), aber als reine Skelettflaeche — die
 * Beschriftungen der Karten sind statisch und stehen erst mit den Daten fest,
 * ein erfundener Text waere eine Behauptung ueber Zahlen, die es noch nicht gibt.
 */
function KennzahlZelle() {
  return (
    <>
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="rounded" width="55%" height={24} sx={{ mt: SP.chip }} />
      <Skeleton variant="text" width="40%" sx={{ mt: SP.chip }} />
    </>
  );
}

function KennzahlKarte() {
  return (
    <KpiRow>
      {[...Array(KENNZAHL_ZELLEN)].map((_, i) => (
        <KpiCell key={i}>
          <KennzahlZelle />
        </KpiCell>
      ))}
    </KpiRow>
  );
}

export default function ScraperListSkeleton({ isEmbedded = false, tableOnly = false }) {
  const verschachtelt = React.useContext(PageContainerContext);

  // 🔴 R15 Runde 3, W-3 (2026-10-21): die drei Reservierungen unter dem Panel
  // stehen NUR, wenn die geladene Seite sie zeigt. Geladen haengen alle drei an
  // `!isEmbedded && !tableOnly` (`ScraperList.jsx`: `tablePaper` fuer die
  // Fusszeile, `analysisMobileLine` und `analysisSection` fuer Zahlenzeile und
  // Kopf). Im eingebetteten Zweig (`/products/:sku`) und in `tableOnly` gibt es
  // sie nicht — dort fielen beim Eintreffen der Daten ~68 px weg, statt dass
  // etwas reserviert wird.
  //
  // 🔴 R16 Runde 2, W-1 (2026-10-21): dasselbe gilt fuer Kennzahl-Reihe UND
  // Filterzeilen. Geladen eingebettet existiert nur `embeddedSection`
  // (`ScraperList.jsx`, `isEmbedded`-Zweig): Tabelle, optionaler Knopf,
  // optionale Blaetterleiste — die KPI-Reihe und die Filter haengen dort
  // ausdruecklich an `!tableOnly && !isEmbedded` bzw. `renderFilters`. Das
  // Geruest zeichnete beides trotzdem, und beim Eintreffen der Daten fiel der
  // Abschnitt zusammen (Produktseite und `CompetitorDetail`).
  //
  // Die Fusszeile wird eingebettet NICHT reserviert: geladen haengt sie an
  // `needsPagination` (`rowsPerPage > 0 && sorted.length > rowsPerPage`,
  // `ScraperList.jsx`) — einer Groesse, die es im Ladezustand nicht gibt. Sie
  // unbedingt zu reservieren waere derselbe Fehler wie W-2 in der
  // Gegenrichtung: Platz fuer etwas, das nicht kommt.
  const nurTabelle = isEmbedded || tableOnly;
  const zeigtFussUndAnalyse = !nurTabelle;

  const tabelle = (
    <Table size="small">
      <TableHead>
        <TableRow>
          {[...Array(TABELLEN_SPALTEN)].map((_, i) => (
            <TableCell key={i}><Skeleton variant="text" width={80} /></TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(TABELLEN_ZEILEN)].map((_, r) => (
          <TableRow key={r}>
            {[...Array(TABELLEN_SPALTEN)].map((__, c) => (
              <TableCell key={c}><Skeleton variant="text" width="70%" /></TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const inhalt = (
    <Stack spacing={SP.section} sx={{ pb: SP.xl }}>
      {!nurTabelle && (
        <Stack spacing={SP.card}>
          {/* Kennzahlen — EINE Karte mit vier Zellen, wie geladen (§2.3). */}
          {[...Array(KENNZAHL_KARTEN)].map((_, r) => (
            <KennzahlKarte key={r} />
          ))}
          {/* 🔴 UI2 Teil 10, G10a: die drei Analyse-Karten stehen hier NICHT mehr.
              Sie sind mit §2.4 in eine EINGEKLAPPTE `CollapsibleSection` UNTER dem
              Panel gewandert. Im Geruest wird nur deren Kopfzeile reserviert (s.
              den Block nach dem `Panel`) — der Koerper ist zugeklappt und darf
              keinen Hinweis-Text erfinden (dieselbe Begruendung wie
              `KennzahlZelle` oben). Damit ist der alte Zeiger auf den
              `SmartAlertCard`-Block gegenstandslos — den Block gibt es nicht mehr
              (Regel 11 §A5, W20: inhaltlich korrigiert, nicht auf eine neue
              Nummer gezogen). */}
        </Stack>
      )}

      {/* Filter + Tabelle in EINER Flaeche — wie geladen (`Panel`).
          🔴 R16 Runde 2, W-1: eingebettet gibt es weder Filterzeile noch
          `Panel` — geladen steht die Tabelle dort direkt in der Flaeche des
          Aufrufers (`ScraperList.jsx`, `embeddedSection`). */}
      {nurTabelle ? (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>{tabelle}</Box>
      ) : (
      <Panel>
        {/* 🔴 G10b (W31): geladen steht hier EINE Leistenzeile (`ListToolbar`,
            Z1) — die zwei handgerollten Zeilen sind mit der Leiste entfallen.
            Das Skelett reserviert dieselbe eine Zeile: Suche (fuellt den Rest),
            zwei Facetten-Pillen, der „Filters"-Oeffner und das Auffrischen.
            Die Marke `scraper-filter-row` traegt die Anatomie und wird von
            `ScraperList.filterzeile.test.jsx` gegen die geladene Leiste
            gehalten. */}
        <Stack spacing={SP.tight} data-testid="scraper-filters">
          <Stack
            data-testid="scraper-filter-row"
            direction={{ xs: 'column', md: 'row' }}
            spacing={SP.tight}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" width={140} height={40} />
            <Skeleton variant="rounded" width={110} height={40} />
            <Skeleton variant="rounded" width={40} height={40} />
          </Stack>
          {/* 🔴 W4 (Regel-15 Runde 1): geladen stehen unter Z1 noch ZWEI
              Pillenreihen (Z2: `FilterPillRow` fuer Achse und Bedingungen) und
              die Zeile der aktiven Filter (Z3: `ActiveFilterBar`). Bis hierher
              reservierte das Geruest nur Z1 — beim Eintreffen der Daten sprang
              die Seite um zwei Zeilen, und die Kalibrierung in
              `ScraperList.filterzeile.test.jsx` konnte es nicht sehen, weil sie
              „1 gegen 1" zaehlte. Die Marken `scraper-filter-row-z2`/`-z3`
              stehen auf beiden Seiten der Zaehlung gegenueber.
              🔴 W10 (Regel-15 Runde 2): die Pillenzahl je Reihe folgt der
              geladenen Geometrie — Achse 2 (`Active only`/`Inactive`),
              Bedingungen 5 (`BEDINGUNGEN`). Vorher reservierte jede Reihe
              fuenf, die Achse war also um drei Pillen zu breit. */}
          <Box
            data-testid="scraper-filter-row-z2"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: SP.chip }}
          >
            {[2, 5].map((pillen, reihe) => (
              <Stack
                key={reihe}
                direction="row"
                spacing={SP.chip}
                sx={{ flexBasis: '100%' }}
                data-testid={`scraper-filter-row-z2-${reihe}`}
              >
                {[...Array(pillen)].map((__, pille) => (
                  <Skeleton
                    key={pille}
                    variant="rounded"
                    width={96}
                    height={32}
                    data-testid="scraper-filter-pille"
                  />
                ))}
              </Stack>
            ))}
          </Box>
          <Box data-testid="scraper-filter-row-z3" sx={{ display: 'flex', gap: SP.chip }}>
            <Skeleton variant="rounded" width={150} height={32} />
            <Skeleton variant="rounded" width={90} height={32} />
          </Box>
        </Stack>
        <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>{tabelle}</Box>
      </Panel>
      )}

      {zeigtFussUndAnalyse && (
        <>
          {/* 🔴 R16 B-2 (Nachbesserung 2, 2026-10-21): die FUSSZEILE wird reserviert.
              Die W-3-Reservierung darunter war richtig gedacht, aber unvollstaendig:
              geladen steht zwischen `Panel` und Analyse-Kopfzeile die Fusszeile
              (`ScraperList.jsx`, `liste-fuss`), und sie ist ein EIGENES Kind des
              `Stack` — sie traegt also dessen Abstand (SP.section = 24 px) mit. Ohne
              Platz hier rueckte die reservierte Analyse-Kopfzeile beim Eintreffen der
              Daten um Fusszeile + Abstand nach unten: genau die Verschiebung, gegen
              die W-3 antritt, nur eine Ebene tiefer.
              Die Hoehe kommt aus `FOOTER_MIN_HEIGHT` (`TableFooterSimple.jsx`) —
              dieselbe Zahl, nicht eine zweite (R15 Runde 3, W-5) — kein Abstand,
              sondern die Nachbildung derselben Anatomie: links ein Text, rechts
              zwei kleine Flaechen (Blaetterleiste). */}
          <Box
            data-testid="scraper-liste-fuss-platz"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: FOOTER_MIN_HEIGHT,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Skeleton variant="text" width={120} />
            <Stack direction="row" spacing={SP.chip}>
              <Skeleton variant="rounded" width={32} height={32} />
              <Skeleton variant="rounded" width={32} height={32} />
            </Stack>
          </Box>

          {/* 🔴 R16 B-2: und die MOBILE Zahlenzeile. Geladen steht sie nur unter `sm`
              zwischen Fusszeile und Sektion (`analyse-zahlen-mobil`) und traegt
              ebenfalls den `Stack`-Abstand. Fehlte sie hier, rueckte die Analyse-
              Kopfzeile am Telefon um eine Caption-Zeile plus Abstand nach unten. */}
          <Box
            data-testid="scraper-analyse-zahlen-platz"
            sx={{ display: { xs: 'block', sm: 'none' } }}
          >
            <Skeleton variant="text" width="78%" height={20} />
          </Box>

          {/* 🔴 W-3 (Nachbesserung 1, 2026-10-20): die eingeklappte Analyse-Kopfzeile
              wird RESERVIERT. Geladen steht unter dem Panel eine `CollapsibleSection`
              mit einer deterministischen Kopfzeile (Titel + Chevron, Hoehe 32 px aus
              `Typography variant="h6"`, `lineHeight` 1.6 bei `1.25rem`). Fehlte sie
              hier, wuechse die Seite beim Eintreffen der Daten um Kopfzeile plus
              `Stack spacing` (24 px) — genau die Verschiebung, gegen die
              `tests/scrapers/lade-rahmen.spec.ts` antritt. Die Sektion startet
              zugeklappt; ihr Koerper ist im Geruest also NICHT zu zeichnen. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            data-testid="scraper-analyse-kopf"
          >
            <Skeleton
              variant="text"
              width="22%"
              height={32}
              data-testid="scraper-analyse-kopf-titel"
              sx={{ flex: '1 1 auto' }}
            />
            <Skeleton variant="circular" width={30} height={30} />
          </Stack>
        </>
      )}
    </Stack>
  );

  // 🔴 Nur wenn das Skelett ALLEIN steht, traegt es seinen eigenen Container.
  // Steht es schon in einem (`ScraperList.jsx`, Lade-Zweig), gibt es den Inhalt
  // direkt heraus — sonst addierte der Theme-Override 16 px auf Mobil.
  return verschachtelt ? inhalt : <PageContainer>{inhalt}</PageContainer>;
}
